<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import device from '@@/bits/device'
import { captureFetchException } from '@@/bits/error_tracker'
import { __, n__ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { navigateTo } from '@@/bits/location'
import { HttpCode } from '@@/enums'
import OzAvatar from '@@/library/v4/components/OzAvatar.vue'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzLoadingText from '@@/library/v4/components/OzLoadingText.vue'
import OzPadletLogo from '@@/library/v4/components/OzPadletLogo.vue'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { fetchJson } from '@@/surface/api_fetch'
import { User } from '@@/types'
import AuthBottomNav from '@@/vuecomponents/AuthBottomNav.vue'
import SkipToContentLink from '@@/vuecomponents/SkipToContentLink.vue'
import { HTTPMethod } from '@padlet/fetch'
import { computed, ref } from 'vue'

const props = defineProps<{
  title: string
  description: string
  user: User
  isEligibleForTrial: boolean
  numberOfTrialDays: number
  xUpgradeUI: boolean
  referralCode: string
}>()

// Benefits list
const benefits = [
  n__('Unlimited padlets for %{days} day', 'Unlimited padlets for %{days} days', props.numberOfTrialDays, {
    days: props.numberOfTrialDays,
  }),
  __('%{fileSize}MB file uploads', { fileSize: 500 }),
  __('%{videoDuration} minute video recordings', { videoDuration: 15 }),
  __('%{audioDuration} minute audio recordings', { audioDuration: 30 }),
  __('API access'),
  __('Priority support'),
]

const globalSnackbarStore = useGlobalSnackbarStore()

const isUserRegistered = computed(() => {
  return props.user.registered_at != null
})

const isGrantingTrial = ref(false)

// Button text based on state
const primaryButtonText = computed(() => {
  if (isGrantingTrial.value) {
    return __('Activating trial...')
  } else if (props.isEligibleForTrial) {
    return __('Start your trial')
  } else if (props.xUpgradeUI) {
    return __('View current plans')
  } else {
    return __('Go home')
  }
})

// Trial grant API call
async function grantTrialAndNavigateToWelcomePage(): Promise<void> {
  isGrantingTrial.value = true

  try {
    const response = (await fetchJson('/trial/grant', {
      method: HTTPMethod.post,
      body: asciiSafeStringify({
        referral_code: props.referralCode,
      }),
    })) as { data?: { attributes?: { redirect_url?: string } } }

    if (response.data?.attributes?.redirect_url) {
      // Navigate to trial welcome page
      navigateTo(response.data.attributes.redirect_url)
    } else {
      isGrantingTrial.value = false
      globalSnackbarStore.genericFetchError()
    }
  } catch (e) {
    isGrantingTrial.value = false
    globalSnackbarStore.genericFetchError()
    if (![HttpCode.BadRequest, HttpCode.Unauthorized, HttpCode.UnprocessableEntity].includes(e.status)) {
      captureFetchException(e, { source: 'ReferralsWelcomeApp' })
    }
  }
}

function handlePrimaryAction(): void {
  trackEvent('Referrals welcome page', `Clicked ${primaryButtonText.value} button`, null, null, {
    referralCode: props.referralCode,
  })
  if (!isUserRegistered.value) {
    navigateTo(`/auth/signup?referral_code=${props.referralCode}`)
  } else if (props.isEligibleForTrial) {
    grantTrialAndNavigateToWelcomePage()
  } else if (props.xUpgradeUI) {
    navigateTo('/dashboard/settings/billing')
  } else {
    navigateTo('/')
  }
}

function handleLogoClick(): void {
  trackEvent('Referrals welcome page', 'Clicked Padlet logo', null, null, {
    referralCode: props.referralCode,
  })
}
</script>

<template>
  <div :class="['relative', 'flex flex-col items-center', 'w-full h-full', 'tablet-portrait:justify-center']">
    <header
      :class="[
        'fixed',
        'top-0',
        'flex',
        'items-center',
        'justify-between',
        'w-full',
        'box-border',
        'h-14',
        'ps-5',
        'pe-6',
      ]"
    >
      <SkipToContentLink />
      <OzPadletLogo @click="handleLogoClick" />

      <!-- User avatar for logged-in users -->
      <div v-if="isUserRegistered" class="flex items-center pt-4">
        <OzAvatar
          class="rounded-full"
          :width="32"
          :height="32"
          :src="props.user.avatar"
          :alt-text-name="props.user.name || props.user.username"
        />
        <div class="flex flex-col ms-3">
          <span
            class="text-body-small text-dark-text-100 dark:text-light-text-100"
            v-html="
              __(
                `<span class='font-semibold'>Logged in as</span><br><span class='text-body-extra-small'>%{user}</span>`,
                {
                  user: props.user.name || props.user.username,
                },
              )
            "
          />
        </div>
      </div>
    </header>

    <main
      id="main-content"
      :class="[
        'fixed bottom-0',
        'flex',
        'w-full box-border',
        'px-4 pt-4 pb-6',
        'tablet-portrait:relative tablet-portrait:w-[390px] tablet-portrait:p-0 tablet-portrait:px-4',
      ]"
      tabindex="-1"
    >
      <p class="sr-only">{{ title }}</p>

      <div :class="['flex flex-col w-full', 'text-dark-text-100 dark:text-light-text-100']">
        <!-- Hero Section -->
        <div class="mb-4">
          <h1 class="w-full text-login-heading-1 font-semibold ms-2 mb-4">
            {{ title }}
          </h1>
          <h2 v-if="description" class="text-body-posts ms-2">
            {{ description }}
          </h2>
        </div>

        <!-- Benefits Box - Only show for new and eligible users -->
        <div v-if="isEligibleForTrial" class="flex flex-col mb-4">
          <p class="text-body font-semibold mb-2 ps-2">{{ __('What you’ll get') }}</p>
          <OzBox :color="OzBoxColors.Secondary" :radius="12" class="p-4">
            <ul class="space-y-3">
              <li v-for="benefit in benefits" :key="benefit" class="flex items-center">
                <OzIcon name="checkmark_thicker" class="text-grape-500 dark:text-canary-500 me-2" :size="16" />
                <span class="text-body">{{ benefit }}</span>
              </li>
            </ul>
          </OzBox>
        </div>

        <!-- CTA Buttons -->
        <div class="flex">
          <OzLoadingText
            v-if="isGrantingTrial"
            :text="primaryButtonText"
            class="text-heading-4 text-center font-semibold px-5 py-2"
          />
          <OzContainedButton
            v-else
            :color-scheme="OzContainedButtonColorScheme.Primary"
            :text="primaryButtonText"
            :should-truncate-text="false"
            :auto-height="true"
            class="w-full tablet-portrait:w-auto"
            @click="handlePrimaryAction"
          />
        </div>
      </div>
    </main>

    <AuthBottomNav v-if="device.desktop" class="fixed start-6 bottom-6 hidden tablet-portrait:flex" />
  </div>
</template>

<style lang="postcss">
@import '@@/styles/4/kit';
@import '@@/styles/4/dot_grid';

* {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

html {
  @extend .dot-grid;
  @apply overflow-hidden;
  @apply bg-left-top; /* goodcheck-disable-line */
  @apply h-screen;

  font-size: 16px;
}

@media screen and (orientation: landscape) {
  html {
    @apply overflow-auto;
  }
}
</style>
